/**
 * Timing and look of the single spark on the launch screen.
 *
 * Kept free of DOM and clocks so it can be tested; the canvas feeds in times. The look
 * mirrors the show's sparks in apps/visuals/src/stage/points/points.frag.glsl, in
 * proportion to the sprite radius.
 */

import { hexToRgb255, palette } from "@ptp/look";

export const HOLD_DURATION_MS = 4000;
export const DRAIN_DURATION_MS = 600;
export const APPEAR_DURATION_MS = 1000;

/**
 * Fly-off look. Mutable so the ?tweak=1 panel can change it live; nothing else writes it.
 * Widths and distances are in spark radii.
 */
export const sparkTuning = {
  flightDurationMs: 900,
  /** How long a point of the trail stays lit after the spark passed it. */
  trailLengthMs: 600,
  /** Width of the bright trail pass at the head, in spark radii; the soft pass is 2.2x. */
  trailWidth: 0.06,
  /** Share of the head width left at the tail. */
  trailTaper: 0.4,
  /** Opacity of the wide soft pass and the narrow bright pass. */
  trailGlow: 0.25,
  trailCore: 0.8,
  emberCount: 10,
  /** How long an ember lives after it was shed. */
  emberLifeMs: 500,
  emberSize: 0.039,
  /** How far an ember drifts off the path over its life. */
  emberSpread: 0.17,
  /** Star spikes: reach in sprite radii, peak brightness, arm width and diagonal share. */
  spikeLength: 1,
  spikeStrength: 0.5,
  spikeWidth: 0.05,
  spikeDiagonal: 1,
  /** ± range of the spike brightness flicker, and how far spikes rock (radians). */
  spikeTwinkle: 0.5,
  spikeSpin: 0.25,
  /** Soft hot centre instead of an opaque core: peak brightness and reach in spark radii. */
  centreGlow: 0.45,
  centreRadius: 0.3,
};

/** The halo. */
export const SPARK_TINT = hexToRgb255(palette.spark);
/** The spikes and hot centre are lifted halfway from this to white. */
export const SPARK_CORE_TINT = hexToRgb255(palette.sparkCore);

export interface GlowProfile {
  /** Sprite radius multiplier, 1 at rest. */
  radius: number;
  /** Overall brightness, 0..1. */
  intensity: number;
  /**
   * Brightness of the hot centre. Unlike `intensity` it barely changes with charge, so an
   * uncharged spark keeps the show's yellow-white centre instead of turning orange.
   */
  centre: number;
  /** Star spike brightness, 0..1; grows a little while charging. */
  spikes: number;
  /** Spike flicker around 1, as in points.vert.glsl. */
  twinkle: number;
  /** Spike rotation in radians. */
  spin: number;
}

/** Peak brightness and falloff exponent of the halo, as the show's uHaloStrength/Falloff. */
const HALO_STRENGTH = 0.5;
const HALO_FALLOFF = 1.8;

export interface GradientStop {
  offset: number;
  color: string;
}

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

/** Fills linearly while held, drains faster when let go. */
export function advanceCharge(charge: number, dtMs: number, holding: boolean): number {
  const rate = holding ? 1 / HOLD_DURATION_MS : -1 / DRAIN_DURATION_MS;
  return clamp01(charge + dtMs * rate);
}

export function glowProfile(charge: number, timeMs: number): GlowProfile {
  const c = clamp01(charge);
  const eased = 1 - (1 - c) ** 2;
  const seconds = timeMs / 1000;
  // Slow breathing at rest, a faint shimmer once charged.
  const breathe = 0.05 * (1 - c) * Math.sin(seconds * ((2 * Math.PI) / 2.4));
  const shimmer = 0.03 * c * Math.sin(seconds * 20);
  const intensity = clamp01(lerp(0.5, 1, eased) + breathe + shimmer);
  // Two unrelated rates, squared, so the spikes mostly sit at a medium level and now and
  // then flare. Same as the show.
  const n = 0.5 + 0.25 * Math.sin(seconds * 1.7) + 0.25 * Math.sin(seconds * 4.3);
  const { spikeTwinkle, spikeSpin } = sparkTuning;
  return {
    radius: lerp(1, 1.7, eased),
    intensity,
    centre: clamp01(1 + breathe),
    // Not scaled by `intensity`: on the show the spikes are always at full strength.
    spikes: clamp01(lerp(0.8, 1, eased) + 4 * shimmer),
    twinkle: lerp(1 - spikeTwinkle, 1 + spikeTwinkle, n * n),
    spin: spikeSpin * Math.sin(seconds * 0.4),
  };
}

/**
 * Star spike brightness at (x, y) in sprite space (-1..1): a long cross plus a shorter
 * diagonal pair. Mirrors the spikes in apps/visuals/src/stage/points/points.frag.glsl.
 */
export function spikeAlpha(x: number, y: number, width: number, diagonal: number): number {
  const px = Math.abs(x);
  const py = Math.abs(y);
  const d = Math.hypot(px, py);
  const arms =
    Math.exp(-py / width) * (1 - Math.min(px, 1)) ** 3 +
    Math.exp(-px / width) * (1 - Math.min(py, 1)) ** 3;
  // Folded into one quadrant, all four diagonals lie on px == py.
  const along = (px + py) * Math.SQRT1_2;
  const across = Math.abs(px - py) * Math.SQRT1_2;
  const diag = Math.exp(-across / width) * Math.max(1 - along * 1.6, 0) ** 3;
  return clamp01((arms + diagonal * diag) * (1 - smoothstep(0.85, 1, d)));
}

/**
 * Samples the shader's halo falloff into radial gradient stops. Like the show there is no
 * opaque core; a soft hot centre, lifted towards white like the spikes, keeps it strong.
 */
export function sparkGradientStops(profile: GlowProfile, samples = 48): GradientStop[] {
  const { centreGlow, centreRadius } = sparkTuning;
  return Array.from({ length: samples + 1 }, (_, i) => {
    const d = i / samples;
    const halo = HALO_STRENGTH * profile.intensity * (1 - smoothstep(0, 1, d)) ** HALO_FALLOFF;
    const hot = centreGlow * profile.centre * (1 - smoothstep(0, centreRadius, d)) ** 2;
    const alpha = clamp01(halo + hot);
    // Weighted by how much each layer contributes, so the centre blends into the halo.
    const share = alpha > 0 ? hot / (halo + hot) : 0;
    const channel = (tint: number, coreTint: number): number =>
      Math.round(lerp(tint, (coreTint + 255) / 2, share));
    const r = channel(SPARK_TINT.r, SPARK_CORE_TINT.r);
    const g = channel(SPARK_TINT.g, SPARK_CORE_TINT.g);
    const b = channel(SPARK_TINT.b, SPARK_CORE_TINT.b);
    return { offset: d, color: `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(4)})` };
  });
}

export interface Flight {
  /** Linear progress, 0..1. */
  t: number;
  /** 0 at the centre, 1 fully above the top edge. */
  rise: number;
  /** Sideways drift, -1..1. */
  sway: number;
}

export function flightProgress(elapsedMs: number): Flight {
  const t = clamp01(elapsedMs / sparkTuning.flightDurationMs);
  return { t, rise: t ** 3, sway: Math.sin(t * Math.PI) };
}

export function appearAlpha(elapsedMs: number): number {
  return smoothstep(0, APPEAR_DURATION_MS, elapsedMs);
}

/**
 * The fly-off trail and embers mirror apps/visuals/src/stage/points/trail.vert.glsl and
 * ember.vert.glsl. The flight is closed-form in time, so the trail is just where the
 * spark was a moment ago - no position history.
 */

export interface TrailSample {
  /** Flight time the spark was here, for `flightProgress`. */
  elapsedMs: number;
  /** Brightness, 0..1. */
  fade: number;
  /** 1 at the spark, 0 at the launch point. */
  head: number;
}

/**
 * Head to tail. Spaced evenly in rise rather than in time: the flight eases in, so even
 * time steps would leave long straight segments at the fast end.
 */
export function trailSamples(elapsedMs: number, segments = 40): TrailSample[] {
  const headRise = flightProgress(elapsedMs).rise;
  if (elapsedMs < 0 || headRise <= 0) return [];
  const samples: TrailSample[] = [];
  for (let k = 0; k <= segments; k++) {
    const along = k / segments;
    const t = (headRise * (1 - along)) ** (1 / 3);
    const sampleMs = t * sparkTuning.flightDurationMs;
    const fade = 1 - clamp01((elapsedMs - sampleMs) / sparkTuning.trailLengthMs);
    samples.push({ elapsedMs: sampleMs, fade: fade * fade, head: 1 - along });
  }
  return samples;
}

/** Share of the flight across which embers are shed. */
const SHED_START = 0.15;
const SHED_END = 0.85;

export interface Ember {
  /** Flight time it was shed at, for `flightProgress`. */
  elapsedMs: number;
  /** Drift away from the path, in spark radii. */
  dx: number;
  dy: number;
  /** Size multiplier around 1. */
  size: number;
  alpha: number;
}

/** Embers alive at `elapsedMs`; `seed` varies the scatter between launches. */
export function emberStates(
  elapsedMs: number,
  seed: number,
  count = sparkTuning.emberCount,
): Ember[] {
  const hash = (n: number): number => {
    const x = Math.sin(n * 12.9898 + seed * 78.233) * 43758.5453;
    return x - Math.floor(x);
  };
  const embers: Ember[] = [];
  for (let j = 0; j < count; j++) {
    const jitter = hash(j * 37 + 1);
    // Shed across the visible middle of the flight, not the slow start or off-screen.
    const shedT = lerp(
      SHED_START,
      SHED_END,
      clamp01(j / Math.max(1, count - 1) + (jitter - 0.5) * 0.1),
    );
    const shedMs = shedT * sparkTuning.flightDurationMs;
    const life = (elapsedMs - shedMs) / sparkTuning.emberLifeMs;
    if (life < 0 || life > 1) continue;

    const angle = hash(j * 91 + 1) * 2 * Math.PI;
    const spread = (0.04 + sparkTuning.emberSpread * life) * (0.4 + hash(j * 17 + 1));
    const flicker = 0.7 + 0.3 * Math.sin((elapsedMs / 1000) * 23 + angle * 5);
    embers.push({
      elapsedMs: shedMs,
      dx: Math.cos(angle) * spread,
      dy: Math.sin(angle) * spread,
      size: 0.6 + jitter,
      alpha: (1 - life) ** 2 * flicker,
    });
  }
  return embers;
}

/** Until the trail and the last ember have faded, from take-off. */
export function flyOffDurationMs(): number {
  const { flightDurationMs, trailLengthMs, emberLifeMs } = sparkTuning;
  return Math.max(flightDurationMs + trailLengthMs, SHED_END * flightDurationMs + emberLifeMs);
}

/**
 * Resting layout of the end screen's idle sparks, in spark radii from the cluster centre.
 * `charge` feeds `glowProfile`: the centre spark glows like a fully charged one.
 */
const IDLE_LAYOUT = [
  { x: 0, y: 0, charge: 1 },
  { x: -1.9, y: -0.9, charge: 0 },
  { x: 1.7, y: -1.2, charge: 0 },
  { x: -1.3, y: 1.4, charge: 0 },
  { x: 1.5, y: 1.1, charge: 0 },
] as const;

/**
 * Pairs of `IDLE_LAYOUT` indices joined by a thin line: the centre spark to the two on
 * its left, and the two on the right to each other.
 */
export const IDLE_LINKS: readonly (readonly [number, number])[] = [
  [0, 1],
  [1, 3],
  [0, 3],
  [0, 4],
  [2, 4],
];

/** How far an idle spark wanders from its resting place, in spark radii. */
export const IDLE_DRIFT = 0.25;

export interface IdleSpark {
  /** Offset from the cluster centre, in spark radii. */
  dx: number;
  dy: number;
  /** Time offset for `glowProfile`, so the sparks breathe out of sync. */
  phaseMs: number;
  /** Charge for `glowProfile`; higher is brighter. */
  charge: number;
}

/** The end screen's idle sparks at `timeMs`; `drift` 0 holds them still (reduced motion). */
export function idleSparks(timeMs: number, drift = IDLE_DRIFT): IdleSpark[] {
  const seconds = timeMs / 1000;
  return IDLE_LAYOUT.map((base, i) => {
    const speed = 0.025 + 0.007 * i;
    const phase = i * 1.7;
    return {
      dx: base.x + drift * Math.sin(seconds * speed * 2 * Math.PI + phase),
      dy: base.y + drift * Math.cos(seconds * speed * 1.3 * 2 * Math.PI + phase * 0.6),
      phaseMs: i * 470,
      charge: base.charge,
    };
  });
}

/** How far a spark of the finished pattern wanders from its dot, in spark radii. */
export const PATTERN_DRIFT = 0.15;

/**
 * Wander of the pattern spark at `index` at `timeMs`; `drift` 0 holds it still. Speeds and
 * phases are spread by a golden-ratio step, so neighbours never move in step.
 */
export function patternSparkOffset(
  index: number,
  timeMs: number,
  drift = PATTERN_DRIFT,
): Pick<IdleSpark, "dx" | "dy" | "phaseMs"> {
  const seconds = timeMs / 1000;
  const spread = (index * 0.618034) % 1;
  const speed = 0.04 + 0.05 * spread;
  const phase = index * 2.399;
  return {
    dx: drift * Math.sin(seconds * speed * 2 * Math.PI + phase),
    dy: drift * Math.cos(seconds * speed * 1.3 * 2 * Math.PI + phase * 0.6),
    phaseMs: spread * 2400,
  };
}
